import React, { CSSProperties, useEffect, useMemo, useState } from "react"

import icBox from "@/assest/ic_box.svg"
import icClose from "@/assest/ic_close.png"
import icComputer from "@/assest/ic_computer.svg"
import icLaptop from "@/assest/ic_laptop.svg"
import icPad from "@/assest/ic_pad.svg"
import icWeb from "@/assest/ic_web.svg"
import icApp from "@/assest/ic_app.svg"
import { API, LoginDeviceRes, PrivacyConfigRes } from "@/api"
import { AutoLogoutTimeGap, HttpAPIError } from "@/api/model"
import { useAppDispatch, useAppSelector } from "@/store"
import { selectFpHash, selectPhoneNum } from "@/stores/auth_slice"
import { selectLoginState } from "@/stores/login_slice"
import { BizError } from "@/models/biz_model"
import { setAutoLogoutTimeGap } from "@/stores/remote_desk_state_slice"
import { AppIconButton, AppSelect, AppSelectOption } from "@/widgets/Common"
import { AppDialog } from "@/widgets/AppDialog"
import { UserUsecase } from "@/usecases/user"
import { AutoLoginCache } from "@/utils/storage"
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Spinner,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react"

const DISABLED_SETTING_TIP = "无法设置此项，请联系IT管理员"

interface PrivacyContentProps {
  onModalOpenChange?: (isOpen: boolean) => void
}

export const PrivacyContent: React.FC<PrivacyContentProps> = ({
  onModalOpenChange,
}) => {
  const dispatch = useAppDispatch()
  const [isOpenDeviceModal, setIsOpenDeviceModal] = useState(false)
  const [privacyConfig, setPrivacyConfig] = useState<PrivacyConfigRes | null>(
    null
  )
  const [updatingConfigKey, setUpdatingConfigKey] = useState<
    keyof PrivacyConfigRes | null
  >(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [pendingSwitchConfirm, setPendingSwitchConfirm] =
    useState<PendingSwitchConfirm | null>(null)
  const phoneNum = useAppSelector(selectPhoneNum)
  const fpHash = useAppSelector(selectFpHash)
  const currentUser = useAppSelector(selectLoginState).currentUser
  const itemStyle: CSSProperties = {
    border: "1px solid #ffffffd9",
    borderRadius: "10px",
    padding: "4px 10px",
    marginBottom: "8px",
  }
  const groupedItemStyle: CSSProperties = {
    border: "1px solid #ffffffd9",
    borderRadius: "10px",
    marginBottom: "8px",
    overflow: "hidden",
  }
  const selectItemStyle: CSSProperties = {
    border: "1px solid #ffffffd9",
    borderRadius: "10px",
    padding: "4px 2px 4px 10px",
    marginBottom: "8px",
  }
  const headerStyle: CSSProperties = {
    color: "#FFFFFF",
    fontSize: "13px",
    whiteSpace: "nowrap",
  }
  const selectTriggerStyle: CSSProperties = {
    background: "#000000",
    boxShadow: "none",
    minHeight: "28px",
  }
  const autoLockOptions: AppSelectOption[] = [
    { label: "永不", value: 0 },
    { label: "5分钟", value: 5 },
    { label: "10分钟", value: 10 },
    { label: "30分钟", value: 30 },
    { label: "1小时", value: 60 },
  ]
  const autoLockConfig = privacyConfig?.auto_lock_minutes

  useEffect(() => {
    onModalOpenChange?.(isOpenDeviceModal || pendingSwitchConfirm !== null)
  }, [isOpenDeviceModal, onModalOpenChange, pendingSwitchConfirm])

  useEffect(() => {
    return () => onModalOpenChange?.(false)
  }, [onModalOpenChange])

  const loadPrivacyConfig = async () => {
    const res = await API.getPrivacyConfig()
    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "隐私配置获取失败")
      return
    }

    setPrivacyConfig(res)
    if (currentUser) {
      AutoLoginCache.updateConfig(currentUser.account, {
        disabledByAdmin: res.auto_login_disable?.value === true,
      })
    }
    dispatch(
      setAutoLogoutTimeGap(
        autoLockMinutesToAutoLogoutTimeGap(res.auto_lock_minutes?.value)
      )
    )
    setErrorMsg("")
  }

  useEffect(() => {
    loadPrivacyConfig().catch(console.error)
  }, [currentUser])

  const handleAutoLockChange = async (option: AppSelectOption) => {
    setUpdatingConfigKey("auto_lock_minutes")
    setErrorMsg("")
    const res = await API.updatePrivacyConfig({
      auto_lock_minutes: option.value as number,
    })
    setUpdatingConfigKey(null)

    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "自动锁屏设置更新失败")
      return
    }

    setPrivacyConfig(res)
    dispatch(
      setAutoLogoutTimeGap(
        autoLockMinutesToAutoLogoutTimeGap(res.auto_lock_minutes?.value)
      )
    )
  }

  const updatePrivacySwitch = async (
    key: "two_factor_enabled" | "data_security_lock_enabled",
    value: boolean
  ) => {
    setUpdatingConfigKey(key)
    setErrorMsg("")
    const res = await API.updatePrivacyConfig({ [key]: value })
    setUpdatingConfigKey(null)

    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "隐私配置更新失败")
      return
    }

    setPrivacyConfig(res)
  }

  const updateAutoLogin = async (value: boolean) => {
    if (!currentUser || !fpHash) {
      setErrorMsg("当前设备信息未准备完成，请稍后重试")
      return
    }
    if (privacyConfig?.auto_login_disable?.value === true) {
      setErrorMsg("自动登录已被管理员禁用")
      return
    }
    setUpdatingConfigKey("auto_login_disable")
    const result = await UserUsecase.setAutoLogin(currentUser, fpHash, value)
    setUpdatingConfigKey(null)
    if (result instanceof BizError) {
      setErrorMsg(result.displayErrorMsg || "自动登录设置失败")
      return
    }
    setErrorMsg("")
    setPrivacyConfig((config) => (config === null ? config : { ...config }))
  }

  const handleAutoLoginChange = (value: boolean) => {
    if (!value) {
      void updateAutoLogin(false)
      return
    }
    setErrorMsg("")
    setPendingSwitchConfirm({
      key: "auto_login",
      value,
      title:
        "开启后，仅对本浏览器登录有效，登录时无需输入账号密码，请确保本设备安全。",
    })
  }

  const handlePrivacySwitchChange = (
    key: "two_factor_enabled" | "data_security_lock_enabled",
    value: boolean
  ) => {
    if (!value) {
      updatePrivacySwitch(key, value).catch(console.error)
      return
    }

    const validateResult =
      key === "two_factor_enabled"
        ? validateBoundPhone(phoneNum)
        : validateBoundPhoneOrEmail(phoneNum)
    if (validateResult !== null) {
      setErrorMsg(validateResult)
      return
    }

    setErrorMsg("")
    setPendingSwitchConfirm({
      key,
      value,
      title:
        key === "two_factor_enabled"
          ? "开启后，登录电脑将需要输入账号密码和手机验证码。"
          : "开启后，提升磁盘数据保密等级，制作镜像、数据迁移，将需要通过您的手机验证码确认授权。",
    })
  }

  const confirmPendingSwitch = () => {
    if (pendingSwitchConfirm === null) {
      return
    }

    if (pendingSwitchConfirm.key === "auto_login") {
      void updateAutoLogin(pendingSwitchConfirm.value)
    } else {
      void updatePrivacySwitch(
        pendingSwitchConfirm.key,
        pendingSwitchConfirm.value
      )
    }
    setPendingSwitchConfirm(null)
  }

  const renderPrivacySwitch = (
    label: string,
    key: "two_factor_enabled" | "data_security_lock_enabled"
  ) => {
    const configItem = privacyConfig?.[key]
    const isEditable = configItem?.editable === true
    const isDisabled = !isEditable || updatingConfigKey === key
    return (
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        padding="7px 10px"
        minHeight="34px"
      >
        <Text style={headerStyle}>{label}</Text>
        <Tooltip label={DISABLED_SETTING_TIP} isDisabled={isEditable} hasArrow>
          <Box as="span">
            <Switch
              isChecked={configItem?.value ?? false}
              isDisabled={isDisabled}
              onChange={(event) => {
                handlePrivacySwitchChange(key, event.target.checked)
              }}
            />
          </Box>
        </Tooltip>
      </Flex>
    )
  }

  return (
    <Flex padding="10px 10px" direction="column">
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={itemStyle}
        minHeight="34px"
      >
        <Text style={headerStyle}>登陆设备管理</Text>
        <Button
          size="xs"
          background="#5998f2e3"
          color="white"
          fontSize="12px"
          _hover={{ background: "#438bf2e3" }}
          onClick={() => setIsOpenDeviceModal(true)}
        >
          查看
        </Button>
      </Flex>

      <Flex
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={selectItemStyle}
        minHeight="34px"
      >
        <Text style={headerStyle}>自动锁屏设置</Text>
        <Box width="160px" flexShrink={0}>
          {autoLockConfig === undefined ? null : !autoLockConfig.editable ? (
            <Text
              color="rgba(255, 255, 255, 0.86)"
              textAlign="right"
              fontSize="12px"
              marginRight="9px"
            >
              {formatAutoLockMinutes(autoLockConfig.value)}
            </Text>
          ) : (
            <Box
              opacity={updatingConfigKey === "auto_lock_minutes" ? 0.55 : 1}
              pointerEvents={
                updatingConfigKey === "auto_lock_minutes" ? "none" : "auto"
              }
            >
              <AppSelect
                key={`auto-lock-${autoLockConfig.value ?? 0}`}
                value={getAutoLockOption(autoLockOptions, autoLockConfig.value)}
                options={autoLockOptions}
                triggerStyle={selectTriggerStyle}
                onChange={(selected) => {
                  handleAutoLockChange(selected).catch(console.error)
                }}
              />
            </Box>
          )}
        </Box>
      </Flex>

      <Flex direction="column" style={groupedItemStyle}>
        <Flex
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          padding="7px 10px"
          minHeight="34px"
        >
          <Text style={headerStyle}>自动登录</Text>
          <Tooltip
            label="自动登录已被管理员禁用"
            isDisabled={privacyConfig?.auto_login_disable?.value !== true}
            hasArrow
          >
            <Box as="span">
              <Switch
                isChecked={
                  currentUser
                    ? AutoLoginCache.getConfig(currentUser.account).enabled
                    : false
                }
                isDisabled={
                  !currentUser ||
                  !fpHash ||
                  privacyConfig?.auto_login_disable?.value === true ||
                  updatingConfigKey === "auto_login_disable"
                }
                onChange={(event) =>
                  handleAutoLoginChange(event.target.checked)
                }
              />
            </Box>
          </Tooltip>
        </Flex>
        <Box height="1px" background="rgba(255, 255, 255, 0.22)" />
        {renderPrivacySwitch("登录双重验证", "two_factor_enabled")}
        <Box height="1px" background="rgba(255, 255, 255, 0.22)" />
        {renderPrivacySwitch("数据安全锁", "data_security_lock_enabled")}
      </Flex>

      {errorMsg.length > 0 ? (
        <Text color="#ff9b9b" fontSize="12px">
          {errorMsg}
        </Text>
      ) : null}

      <LoginDevicesModal
        isOpen={isOpenDeviceModal}
        onClose={() => setIsOpenDeviceModal(false)}
      />
      <AppDialog
        title={pendingSwitchConfirm?.title ?? ""}
        isOpen={pendingSwitchConfirm !== null}
        width={pendingSwitchConfirm?.key === "auto_login" ? "310px" : undefined}
        onConfirm={confirmPendingSwitch}
        onCancel={() => setPendingSwitchConfirm(null)}
      />
    </Flex>
  )
}

interface LoginDevicesModalProps {
  isOpen: boolean
  onClose: () => void
}

const LoginDevicesModal: React.FC<LoginDevicesModalProps> = (props) => {
  const { isOpen, onClose } = props
  const [devices, setDevices] = useState<LoginDeviceRes[]>([])
  const [privacyConfig, setPrivacyConfig] = useState<PrivacyConfigRes | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdatingScope, setIsUpdatingScope] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [trustAction, setTrustAction] = useState<TrustActionState | null>(null)
  const fpHash = useAppSelector(selectFpHash)
  const phoneNum = useAppSelector(selectPhoneNum)

  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      const aCurrentDevice = isCurrentLoginDevice(a, fpHash)
      const bCurrentDevice = isCurrentLoginDevice(b, fpHash)
      if (aCurrentDevice !== bCurrentDevice) {
        return aCurrentDevice ? -1 : 1
      }

      if (a.trusted !== b.trusted) {
        return a.trusted ? -1 : 1
      }

      return new Date(b.loginAt).getTime() - new Date(a.loginAt).getTime()
    })
  }, [devices, fpHash])

  const onlyTrustedDeviceLogin =
    privacyConfig?.login_device_scope.value === LOGIN_DEVICE_SCOPE_TRUSTED_ONLY
  const isLoginDeviceScopeEditable =
    privacyConfig?.login_device_scope.editable === true
  const isLoginDeviceScopeDisabled =
    isUpdatingScope || !isLoginDeviceScopeEditable

  const loadData = async () => {
    setIsLoading(true)
    setErrorMsg("")
    const [deviceRes, configRes] = await Promise.all([
      API.getLoginDevices(),
      API.getPrivacyConfig(),
    ])

    if (deviceRes instanceof HttpAPIError) {
      setErrorMsg(deviceRes.msg ?? "登录设备列表获取失败")
    } else {
      setDevices(deviceRes)
    }

    if (configRes instanceof HttpAPIError) {
      setErrorMsg(configRes.msg ?? "隐私配置获取失败")
    } else {
      setPrivacyConfig(configRes)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (isOpen) {
      loadData().catch(console.error)
    }
  }, [isOpen])

  const updateLoginDeviceScope = async (checked: boolean) => {
    setIsUpdatingScope(true)
    setErrorMsg("")
    const res = await API.updatePrivacyConfig({
      login_device_scope: checked
        ? LOGIN_DEVICE_SCOPE_TRUSTED_ONLY
        : LOGIN_DEVICE_SCOPE_ANY,
    })
    setIsUpdatingScope(false)

    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "登录设备范围更新失败")
      return
    }

    setPrivacyConfig(res)
  }

  const refreshDevices = async () => {
    const res = await API.getLoginDevices()
    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "登录设备列表刷新失败")
      return
    }
    setDevices(res)
  }

  const closeTrustAction = () => {
    setTrustAction(null)
  }

  const openTrustAction = (device: LoginDeviceRes, trusted: boolean) => {
    const validateResult = validateBoundPhone(phoneNum)
    if (validateResult !== null) {
      setErrorMsg(validateResult)
      return
    }

    setErrorMsg("")
    setTrustAction({ device, trusted })
  }

  if (!isOpen) {
    return <></>
  }

  return (
    <Flex
      id="full-screen-unvideo-element-tag"
      width="100vw"
      height="100vh"
      position="fixed"
      left={0}
      top={0}
      backgroundColor="rgba(0, 0, 0, 0.55)"
      justifyContent="center"
      alignItems="center"
      zIndex={9999}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Flex
        id="full-screen-unvideo-element-tag"
        direction="column"
        width="560px"
        maxWidth="92vw"
        height="620px"
        maxHeight="86vh"
        backgroundColor="rgba(59, 54, 54, 1)"
        borderRadius="8px"
        overflow="hidden"
        boxShadow="0 12px 36px rgba(0, 0, 0, 0.35)"
      >
        <Flex
          minHeight="40px"
          alignItems="center"
          justifyContent="space-between"
          padding="0 14px"
        >
          <Text color="white" fontSize="14px" fontWeight={600}>
            登陆设备管理
          </Text>
          <AppIconButton
            src={icClose}
            size="22px"
            padding="4px"
            onClick={onClose}
          />
        </Flex>

        <Flex
          alignItems="center"
          minHeight="34px"
          padding="0 18px"
          background="rgba(80, 151, 210, 0.32)"
          color="#d9f0ff"
          fontSize="12px"
        >
          <Box
            width="14px"
            height="14px"
            borderRadius="50%"
            border="1px solid #d9f0ff"
            textAlign="center"
            lineHeight="12px"
            marginRight="8px"
            flexShrink={0}
          >
            i
          </Box>
          以下是最近登陆过的设备，您可以管理设备的信任状态。
        </Flex>

        <Box padding="12px 18px 0" overflowY="auto" flex="1">
          {isLoading ? (
            <Flex height="220px" alignItems="center" justifyContent="center">
              <Spinner color="white" />
            </Flex>
          ) : sortedDevices.length === 0 ? (
            <Flex height="220px" alignItems="center" justifyContent="center">
              <Text color="rgba(255, 255, 255, 0.72)" fontSize="13px">
                暂无登录设备
              </Text>
            </Flex>
          ) : (
            <Flex direction="column">
              {sortedDevices.map((device, index) => (
                <LoginDeviceItem
                  key={device.deviceID || `login-device-${index}`}
                  device={device}
                  isCurrentDevice={isCurrentLoginDevice(device, fpHash)}
                  onTrustAction={(trusted) => openTrustAction(device, trusted)}
                />
              ))}
            </Flex>
          )}
        </Box>
        <Flex
          height="42px"
          alignItems="center"
          justifyContent="space-between"
          padding="0 18px"
          color="rgba(255, 255, 255, 0.86)"
          fontSize="12px"
        >
          <Text
            color="#ff9b9b"
            fontSize="12px"
            noOfLines={1}
            marginRight="12px"
          >
            {errorMsg}
          </Text>
          <Tooltip
            label={DISABLED_SETTING_TIP}
            isDisabled={isLoginDeviceScopeEditable}
            hasArrow
          >
            <Flex as="span" alignItems="center" gap="6px">
              <Text>只允许已信任设备登录</Text>
              <Checkbox
                size="sm"
                colorScheme="blue"
                isChecked={onlyTrustedDeviceLogin}
                isDisabled={isLoginDeviceScopeDisabled}
                iconColor="white"
                iconSize="0.8rem"
                onChange={(event) => {
                  updateLoginDeviceScope(event.target.checked).catch(
                    console.error
                  )
                }}
              />
            </Flex>
          </Tooltip>
        </Flex>
      </Flex>
      <TrustDeviceVerifyModal
        action={trustAction}
        onClose={closeTrustAction}
        onSuccess={() => {
          closeTrustAction()
          refreshDevices().catch(console.error)
        }}
      />
    </Flex>
  )
}

interface LoginDeviceItemProps {
  device: LoginDeviceRes
  isCurrentDevice: boolean
  onTrustAction: (trusted: boolean) => void
}

const LoginDeviceItem: React.FC<LoginDeviceItemProps> = (props) => {
  const { device, isCurrentDevice, onTrustAction } = props
  return (
    <Flex
      minHeight="110px"
      alignItems="center"
      padding="12px 0"
      borderBottom="1px solid rgba(255, 255, 255, 0.12)"
      color="white"
      fontSize="12px"
      gap="16px"
    >
      <Flex
        width="36px"
        height="36px"
        borderRadius="50%"
        background="rgba(255, 255, 255, 0.12)"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <DeviceTypeIcon deviceType={device.deviceType} />
      </Flex>
      <Box flex="1" minWidth={0}>
        <Flex alignItems="center" gap="8px" marginBottom="8px">
          <Text noOfLines={1} fontSize="15px" fontWeight={700}>
            {device.deviceName || "未命名设备"} -{" "}
            {device.clientVersion || "暂无版本数据"}
          </Text>
          {isCurrentDevice ? (
            <Box
              color="#FFFFFF"
              background="rgba(93, 158, 225, 0.75)"
              borderRadius="8px"
              padding="1px 8px"
              fontSize="11px"
              flexShrink={0}
            >
              当前设备
            </Box>
          ) : null}
          {device.trusted ? (
            <Box
              color="#FFFFFF"
              border="1px solid rgba(255, 255, 255, 0.62)"
              borderRadius="8px"
              padding="1px 8px"
              fontSize="11px"
              flexShrink={0}
            >
              信任设备
            </Box>
          ) : null}
        </Flex>

        <Text color="rgba(255, 255, 255, 0.86)">SN : {device.deviceID}</Text>
        <Text color="rgba(255, 255, 255, 0.86)">
          IP : {formatDeviceIp(device)}
        </Text>
        <Text color="rgba(255, 255, 255, 0.86)" marginBottom="4px">
          登录时间 : {formatLoginTime(device.loginAt)}
        </Text>
        {device.trustedAt ? (
          <Text color="rgba(255, 255, 255, 0.86)" marginBottom="4px">
            信任时间 : {formatLoginTime(device.trustedAt)}
          </Text>
        ) : null}
      </Box>
      <Box width="86px" textAlign="right" flexShrink={0} paddingRight="2px">
        {device.deviceID ? (
          <Button
            height="30px"
            width="74px"
            minWidth="74px"
            background={
              device.trusted ? "rgba(255, 255, 255, 0.08)" : "#4aa3ff"
            }
            color="white"
            border={
              device.trusted ? "1px solid rgba(255, 255, 255, 0.4)" : "none"
            }
            fontSize="12px"
            borderRadius="4px"
            _hover={{
              background: device.trusted
                ? "rgba(255, 255, 255, 0.14)"
                : "#3094f5",
            }}
            onClick={() => onTrustAction(!device.trusted)}
          >
            {device.trusted ? "取消信任" : "信任"}
          </Button>
        ) : null}
      </Box>
    </Flex>
  )
}

interface TrustActionState {
  device: LoginDeviceRes
  trusted: boolean
}

interface TrustDeviceVerifyModalProps {
  action: TrustActionState | null
  onClose: () => void
  onSuccess: () => void
}

const SMS_COUNTDOWN_SECONDS = 60
let verificationSmsToken = ""
let verificationSmsCooldownEndAt = 0

function getVerificationSmsCountdown(): number {
  return Math.max(
    0,
    Math.ceil((verificationSmsCooldownEndAt - Date.now()) / 1000)
  )
}

const TrustDeviceVerifyModal: React.FC<TrustDeviceVerifyModalProps> = (
  props
) => {
  const { action, onClose, onSuccess } = props
  const [captchaId, setCaptchaId] = useState("")
  const [captchaPic, setCaptchaPic] = useState("")
  const [captcha, setCaptcha] = useState("")
  const [smsCode, setSmsCode] = useState("")
  const [smsToken, setSmsToken] = useState(verificationSmsToken)
  const [errorMsg, setErrorMsg] = useState("")
  const [captchaErrorMsg, setCaptchaErrorMsg] = useState("")
  const [smsSuccessMsg, setSmsSuccessMsg] = useState(
    getVerificationSmsCountdown() > 0 ? "短信验证码已发送" : ""
  )
  const [smsCountdown, setSmsCountdown] = useState(
    getVerificationSmsCountdown()
  )
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false)
  const [isSendingSms, setIsSendingSms] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const phoneNum = useAppSelector(selectPhoneNum)
  const maskedPhone = formatMaskedPhone(phoneNum)

  const loadCaptcha = async (resetForm = true, clearCaptchaError = true) => {
    setIsLoadingCaptcha(true)
    setErrorMsg("")
    if (clearCaptchaError) {
      setCaptchaErrorMsg("")
    }
    if (resetForm) {
      setCaptcha("")
      setSmsCode("")
      const countdown = getVerificationSmsCountdown()
      setSmsToken(countdown > 0 ? verificationSmsToken : "")
      setSmsSuccessMsg(countdown > 0 ? "短信验证码已发送" : "")
      setSmsCountdown(countdown)
    }
    const res = await API.getImageCaptcha()
    setIsLoadingCaptcha(false)

    if (res instanceof HttpAPIError) {
      setCaptchaErrorMsg(res.msg ?? "图形验证码获取失败")
      return
    }

    setCaptchaId(res.captchaId)
    setCaptchaPic(res.picPath)
  }

  useEffect(() => {
    if (action !== null) {
      loadCaptcha().catch(console.error)
    }
  }, [action?.device.deviceID, action?.trusted])

  useEffect(() => {
    if (smsCountdown <= 0) {
      verificationSmsToken = ""
      verificationSmsCooldownEndAt = 0
      return
    }

    const timer = window.setTimeout(() => {
      const countdown = getVerificationSmsCountdown()
      setSmsCountdown(countdown)
      if (countdown <= 0) {
        verificationSmsToken = ""
        setSmsToken("")
        setSmsSuccessMsg("")
      }
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [smsCountdown])

  const sendSms = async () => {
    if (captchaId.length === 0 || captcha.trim().length === 0) {
      setCaptchaErrorMsg("请输入图片验证码")
      return
    }

    setIsSendingSms(true)
    setErrorMsg("")
    setCaptchaErrorMsg("")
    setSmsSuccessMsg("")

    const verifyRes = await API.captchaVerify(captchaId, captcha.trim())
    if (verifyRes instanceof HttpAPIError || !verifyRes.verified) {
      setIsSendingSms(false)
      setCaptchaErrorMsg(
        verifyRes instanceof HttpAPIError
          ? (verifyRes.msg ?? "图形验证码验证失败")
          : "图形验证码验证失败"
      )
      loadCaptcha(false, false).catch(console.error)
      return
    }

    const res = await API.sendVerificationCode({
      captchaId,
      captcha: captcha.trim(),
      phone: phoneNum || undefined,
      type: "trusted_device",
    })
    setIsSendingSms(false)

    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "短信验证码发送失败")
      loadCaptcha(false).catch(console.error)
      return
    }

    verificationSmsToken = res.token
    verificationSmsCooldownEndAt = Date.now() + SMS_COUNTDOWN_SECONDS * 1000
    setSmsToken(res.token)
    setSmsSuccessMsg("短信验证码已发送")
    setSmsCountdown(getVerificationSmsCountdown())
  }

  const submit = async () => {
    if (action === null) {
      return
    }

    if (smsToken.length === 0) {
      setErrorMsg("请先发送短信验证码")
      return
    }

    if (smsCode.trim().length === 0) {
      setErrorMsg("请输入短信验证码")
      return
    }

    setIsSubmitting(true)
    setErrorMsg("")
    const res = await API.updateTrustedDevice(
      action.device.deviceID,
      action.trusted,
      smsCode.trim(),
      smsToken
    )
    setIsSubmitting(false)

    if (res instanceof HttpAPIError) {
      setErrorMsg(res.msg ?? "信任设备更新失败")
      return
    }

    onSuccess()
  }

  if (action === null) {
    return <></>
  }

  return (
    <Flex
      id="full-screen-unvideo-element-tag"
      position="fixed"
      left={0}
      top={0}
      width="100vw"
      height="100vh"
      backgroundColor="rgba(0, 0, 0, 0.35)"
      alignItems="center"
      justifyContent="center"
      zIndex={10000}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Flex
        id="full-screen-unvideo-element-tag"
        direction="column"
        width="430px"
        backgroundColor="rgba(59, 54, 54, 1)"
        borderRadius="12px"
        padding="24px"
        gap="18px"
      >
        <Flex justifyContent="center" alignItems="center" position="relative">
          <Text color="white" fontSize="28px" fontWeight={600}>
            身份验证
          </Text>
          <Box position="absolute" right={0}>
            <AppIconButton
              src={icClose}
              size="22px"
              padding="4px"
              onClick={onClose}
            />
          </Box>
        </Flex>
        <Text
          color="rgba(255, 255, 255, 0.86)"
          fontSize="14px"
          textAlign="center"
        >
          验证码将发送至：{maskedPhone}
        </Text>
        <Text color="white" fontSize="15px" fontWeight={600}>
          图片验证码{" "}
          <Text as="span" color="#ff8f8f">
            *
          </Text>
          <Text as="span" color="rgba(255, 255, 255, 0.72)" fontWeight={400}>
            （不区分大小写）
          </Text>
        </Text>
        <Flex gap="10px" alignItems="flex-start">
          <Box flex="1">
            <Input
              height="44px"
              flex="1"
              color="white"
              value={captcha}
              _placeholder={{ color: "rgba(255, 255, 255, 0.82)" }}
              onChange={(event) => {
                setCaptcha(event.target.value)
                setSmsToken("")
                setCaptchaErrorMsg("")
              }}
              placeholder="请输入图片验证码"
              onKeyDown={stopRemoteKeyboardEvent}
              onKeyUp={stopRemoteKeyboardEvent}
            />
            {captchaErrorMsg.length > 0 ? (
              <Text color="#ff9b9b" fontSize="12px" marginTop="6px">
                {captchaErrorMsg}
              </Text>
            ) : null}
          </Box>
          <Flex direction="column" alignItems="center" width="120px">
            <Flex
              width="120px"
              height="44px"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingCaptcha ? (
                <Spinner size="sm" color="white" />
              ) : captchaPic.length > 0 ? (
                <img
                  src={captchaPic}
                  style={{
                    width: "120px",
                    height: "44px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                "重新获取"
              )}
            </Flex>
            <Button
              size="sm"
              variant="link"
              color="#7eb6ff"
              marginTop="6px"
              onClick={() => {
                loadCaptcha(false).catch(console.error)
              }}
            >
              重新获取
            </Button>
          </Flex>
        </Flex>
        <Text color="white" fontSize="15px" fontWeight={600}>
          短信验证码{" "}
          <Text as="span" color="#ff8f8f">
            *
          </Text>
        </Text>
        <Flex gap="10px" alignItems="center">
          <Input
            height="44px"
            flex="1"
            color="white"
            placeholder="请输入短信验证码"
            _placeholder={{ color: "rgba(255, 255, 255, 0.82)" }}
            value={smsCode}
            onChange={(event) => setSmsCode(event.target.value)}
            onKeyDown={stopRemoteKeyboardEvent}
            onKeyUp={stopRemoteKeyboardEvent}
          />
          <Button
            height="44px"
            width="120px"
            colorScheme="blue"
            isLoading={isSendingSms}
            isDisabled={smsCountdown > 0}
            onClick={() => {
              sendSms().catch(console.error)
            }}
          >
            {smsCountdown > 0
              ? `${smsCountdown}s`
              : smsToken.length > 0
                ? "重新获取"
                : "获取验证码"}
          </Button>
        </Flex>
        {smsSuccessMsg.length > 0 ? (
          <Text color="#8fe88f" fontSize="12px">
            {smsSuccessMsg}
          </Text>
        ) : null}
        {errorMsg.length > 0 ? (
          <Text color="#ff9b9b" fontSize="12px">
            {errorMsg}
          </Text>
        ) : null}
        <Flex justifyContent="flex-end" gap="10px">
          <Button
            width="100%"
            height="46px"
            colorScheme="blue"
            isLoading={isSubmitting}
            isDisabled={smsToken.length === 0 || smsCode.trim().length === 0}
            onClick={() => {
              submit().catch(console.error)
            }}
          >
            提交
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

const LOGIN_DEVICE_SCOPE_ANY = 1
const LOGIN_DEVICE_SCOPE_TRUSTED_ONLY = 2
const PHONE_UNBOUND_TIP = "未绑定手机号，请先联系IT管理员绑定手机号"
const PHONE_OR_EMAIL_UNBOUND_TIP =
  "未绑定手机号/邮箱，请先联系IT管理员绑定手机号/邮箱"
const PHONE_REGEXP = /^1[3-9]\d{9}$/
const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface PendingSwitchConfirm {
  key: "auto_login" | "two_factor_enabled" | "data_security_lock_enabled"
  value: boolean
  title: string
}

function stopRemoteKeyboardEvent(event: React.KeyboardEvent<HTMLInputElement>) {
  event.stopPropagation()
}

function isCurrentLoginDevice(device: LoginDeviceRes, fpHash: string): boolean {
  return Boolean(device.deviceID) && device.deviceID === fpHash
}

function validateBoundPhone(phone: string): string | null {
  const value = phone.trim()
  if (value.length === 0 || !/^\d+$/.test(value)) {
    return PHONE_UNBOUND_TIP
  }

  if (!PHONE_REGEXP.test(value)) {
    return "当前手机号格式不正确"
  }

  return null
}

function validateBoundPhoneOrEmail(value: string): string | null {
  const trimmedValue = value.trim()
  if (trimmedValue.length === 0) {
    return PHONE_OR_EMAIL_UNBOUND_TIP
  }

  if (trimmedValue.includes("@")) {
    return EMAIL_REGEXP.test(trimmedValue) ? null : "邮箱格式不正确"
  }

  return PHONE_REGEXP.test(trimmedValue) ? null : "手机号格式不正常"
}

function autoLockMinutesToAutoLogoutTimeGap(value?: number): AutoLogoutTimeGap {
  switch (value) {
    case 5:
      return "five_minutes"
    case 10:
      return "ten_minutes"
    case 30:
      return "thirty_minutes"
    case 60:
      return "one_hour"
    case 0:
    case undefined:
      return "time_never"
    default:
      return "time_never"
  }
}

function getAutoLockOption(
  options: AppSelectOption[],
  value?: number
): AppSelectOption {
  const normalizedValue = value ?? 0
  const option = options.find((item) => item.value === normalizedValue)
  return (
    option ?? {
      label: formatAutoLockMinutes(normalizedValue),
      value: normalizedValue,
    }
  )
}

function formatAutoLockMinutes(value: number): string {
  if (value === 0) {
    return "永不"
  }

  if (value >= 60 && value % 60 === 0) {
    return `${value / 60}小时`
  }

  return `${value}分钟`
}

// function formatDeviceType(deviceType: number): string {
//   switch (deviceType) {
//     case 0:
//       return "未知"
//     case 1:
//       return "盒子（Linux）"
//     case 2:
//       return "浏览器"
//     case 3:
//       return "kingsoft book"
//     case 4:
//       return "平板"
//     case 5:
//       return "盒子"
//     case 6:
//       return "一体机"
//     default:
//       return "其他"
//   }
// }

const DeviceTypeIcon: React.FC<{ deviceType: number }> = (props) => {
  const { deviceType } = props
  const icon = DEVICE_TYPE_ICON_MAP[deviceType] ?? icComputer
  return (
    <img
      src={icon}
      style={{ width: "24px", height: "24px", objectFit: "contain" }}
    />
  )
}

const DEVICE_TYPE_ICON_MAP: Record<number, string> = {
  0: icComputer,
  1: icBox,
  2: icWeb,
  3: icLaptop,
  4: icPad,
  5: icBox,
  6: icComputer,
  7: icApp,
  8: icApp,
}

function formatMaskedPhone(phone: string): string {
  if (/^\d{11}$/.test(phone)) {
    return `${phone.slice(0, 3)}****${phone.slice(7)}`
  }

  return "当前绑定手机号"
}

function formatDeviceIp(device: LoginDeviceRes): string {
  const location = [device.province, device.isp].filter(Boolean).join(" ")
  if (location.length === 0) {
    return device.ip || "-"
  }

  return `${device.ip || "-"} ( ${location} )`
}

function formatLoginTime(loginAt: string): string {
  if (!loginAt) {
    return "-"
  }

  const date = new Date(loginAt)
  if (Number.isNaN(date.getTime())) {
    return loginAt
  }

  const pad = (value: number) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`
}
